import { AfterViewInit, ChangeDetectorRef, Component, Inject, NgZone, ViewEncapsulation } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { Observable, of } from "rxjs";
import { ImageGalleryComponent } from "../image-gallery.component";
import { LocalStorageService } from "../../../viewer-components/services/internal-services/local-storage.service";
import { IMetadataImage } from "../../../viewer-components/interfaces/i-metadataimage";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { LayerService } from "../../../viewer-components/services/internal-services/layer.service";
import { MapService } from "../../../viewer-components/services/internal-services/map.service";
import { FeatureFlagsService } from "../../../viewer-components/services/internal-services/feature-flags.service";
import { IGalleryStatus } from "../../../viewer-components/interfaces/i-gallery-status";
import { GeoService } from "../../../viewer-components/services/internal-services/geo.service";
import { IGalleryCollection } from "../../../viewer-components/interfaces/i-gallery-collection";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";

@Component({
    selector: "app-phoenix-dialog-gallery",
    templateUrl: "./dialog-gallery.component.html",
    styleUrls: ["./dialog-gallery.component.scss"],
    providers: [MatDialogConfig, MapService],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class DialogGalleryComponent extends ImageGalleryComponent implements AfterViewInit {
  isFullscreenLoaderShown = false;

  fullscreenImage: IMetadataImage = null;

  fullscreenModeIsOn = false;

  fullscreenImageSerialNumber: number = null;

  isLoading: Object = {};

  openFromDock = false;

  obliqueAvailable = true;

  mode = ViewerConstants.IMAGE_GALLERY_MODES.DIALOG;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<DialogGalleryComponent>,
    private ngZone: NgZone,
    private storageService: LocalStorageService,
    layerService: LayerService,
    mapService: MapService,
    protected cdr: ChangeDetectorRef,
    protected flagService: FeatureFlagsService,
  ) {
    super(
      data.viewerService,
      data.googleAnalyticsService,
      data.galleryService,
      layerService,
      mapService,
      cdr,
      flagService,
    );
  }

  ngAfterViewInit() {
    this.dialogRef.keydownEvents().subscribe((event) => {
      if (event.key === "Escape") {
        if (this.fullscreenModeIsOn) {
          this.closeFullscreen();
        } else {
          this.closeGallery();
        }
      }
    });
    this.subscriptions.push(
      this.galleryService.collections$.subscribe((result) => {
        this.collections = result;
        result.forEach((collection) => {
          this.isLoading[collection.year] = true;
        });
      }),
    );
    this.subscriptions.push(
      this.galleryService.status$.subscribe((status) => {
        this.status = status;
      }),
    );
    this.subscriptions.push(
      this.galleryService.selectedFilter$.subscribe((filter) => {
        this.selectedFilter = filter;
      }),
    );
    this.subscriptions.push(
      this.galleryService.selectedCollection$.subscribe((collection) => {
        this.selectedCollection = collection;
      }),
    );
    this.subscriptions.push(
      this.galleryService.fullscreenImage$.subscribe((result) => {
        this.fullscreenImage = result;
      }),
    );
    this.subscriptions.push(
      this.galleryService.allImages$.subscribe((images: IMetadataImage[]) => {
        this.images = images;
      }),
    );
    this.subscriptions.push(
      this.galleryService.filteredImages$.subscribe((images: IMetadataImage[]) => {
        this.filteredImages = images;
      }),
    );
    this.subscriptions.push(
      this.galleryService.fullscreenImageSerialNumber$.subscribe((result) => {
        this.fullscreenImageSerialNumber = result;
      }),
    );
    this.subscriptions.push(
      this.galleryService.isFullscreenModeOn$.subscribe((result) => {
        this.fullscreenModeIsOn = result;
      }),
    );

    this.subscriptions.push(
      this.galleryService.closeDialog$.subscribe((close) => {
        if (close) {
          this.closeDialog();
        }
      }),
    );
    this.dialogRef.backdropClick().subscribe(() => {
      this.closeGallery();
    });
    this.initialize();
  }

  // region Load Data
  initialize(): void {
    this.getInitData().subscribe((status) => {
      this.openGallery(status);
    });
  }

  protected getInitData(): Observable<IGalleryStatus> {
    if (this.data.status) {
      this.openFromDock = true;
      return of(this.data.status);
    }
    let images = this.data.images
      ? this.data.images
      : this.galleryService
          .getGalleryStatus()
          .collections.reduce((acc, collection) => acc.concat(collection.images), []);
    const extent = this.data.viewExtent
      ? this.data.viewExtent
      : GeoService.toWgs84(this.galleryService.getGalleryStatus().extent);
    const address = this.data.address ? this.data.address : this.galleryService.getGalleryStatus().address;
    const center = this.data.center
      ? this.data.center
      : GeoService.toWgs84(this.galleryService.getGalleryStatus().center);

    images = this.filterVisibleImages(images);
    return this.galleryService.calculateInitStatus(images, extent, center, address);
  }

  /**
   * Phoenix viewer the images are not filtered, always oblique mode available
   * @param images
   */
  filterVisibleImages(images: IMetadataImage[]): IMetadataImage[] {
    images = images.filter((image) => !image.archived);
    this.galleryService.setObliqueAvailable(images.length > 0);
    return images;
  }

  processGalleryStatus(status: IGalleryStatus): Observable<IGalleryCollection> {
    this.galleryService.setGalleryStatus(status);
    return this.galleryService.selectCollection(this.galleryService.getInitCollection(status.collections));
  }

  openGallery(status: IGalleryStatus): void {
    this.galleryService.setDialogOpen(true);
    this.processGalleryStatus(status).subscribe(() => {
      // make changes on the UX after open the gallery
      setTimeout(() => {
        this.selectImage = this.selectedImage;
      }, 500);
      const fullscreenImage = this.storageService.get("fullscreen");
      const fullscreenNumber = this.storageService.get("serialNumber");
      if (fullscreenImage && fullscreenNumber) {
        this.galleryService.showFullscreen(fullscreenImage, fullscreenNumber);
      }
      this.cdr.detectChanges();
    });
  }

  // endregion

  // region UI events
  closeGallery(): void {
    this.galleryService.setDisplayGallery(false);
    this.isFullscreenLoaderShown = true;
    this.ngZone.run(() => {
      this.closeDialog();
      this.isFullscreenLoaderShown = false;
      if (!this.openFromDock) {
        this.galleryService.removeGalleryData();
      }
    });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  selectCollection(collection: IGalleryCollection): void {
    this.galleryService.selectCollection(collection);

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.IMAGE_GALLERY_YEAR_FILTER,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  closeCollection(): void {
    if (!this.selectedCollection) {
      this.galleryService.setDisplayGallery(false);
      this.closeGallery();
    }
    // Reset isLoading default value
      Object.keys(this.isLoading). forEach((key) => {
        this.isLoading[key] = true;
      });

    this.ngZone.run(() => {
      this.selectedCollection = null;
      this.storageService.remove("collection");
      this.storageService.remove("filters");
      this.storageService.remove("scroll");
    });
  }

  hideLoaders(key: number): void {
    this.isLoading[key] = false;
  }

  // endregion
}
